import type { AiPredictionRead } from '@timelyapp/integration-cloud-api-spec';
import type { StandardInterval } from '@timelyapp/integration-cloud-sdk/utils';
import { isEqual } from 'lodash-es';
import type { Hour } from 'types/hour';

type InputHour = Hour<number>;
/** Exported for test usage */
export type MinimalHourForMatching = {
  id: InputHour['id'];
  day: InputHour['day'];
  note: InputHour['note'];
  locked: InputHour['locked'];
  manage: InputHour['manage'];
  project: InputHour['project'];
  label_ids: InputHour['label_ids'];
};

/**
 * Returns `hour.id` (or null) of first mutable  time entry/`hour` that
 * matches a (valid) prediction's day, project, and (less strictly) labels.
 */
export const ticPredictionMatchesHourId = (
  prediction: StandardInterval<AiPredictionRead>,
  hours: MinimalHourForMatching[],
  options?: {
    /**
     * Be less strict when trying to associate prediction to a time entry,
     * e.g allowing `hour.label_ids: []` to be extended by `prediction.timely_label_ids: [1, 2]`
     * e.g allowing `hour.label_ids: [1, 2]` to be extended by `prediction.timely_label_ids: [2]`
     */
    relaxed_tag_matching?: boolean;
    /** Does not require `hour.note` to match `prediction.note` */
    relaxed_note_matching?: boolean;
  }
): number | null => {
  const { relaxed_tag_matching = false, relaxed_note_matching = false } = options || {};
  const { inferred } = prediction;

  // AI tell us if the prediction is (likely) valid as a time entry
  if (!inferred.valid_as_hour) return null;

  const dayOfPrediction = prediction.started_at.toISODate();

  const matchingHour = hours.find(hour => {
    if (!hour.manage) return false;
    if (hour.locked) return false;
    if (hour.day !== dayOfPrediction) return false;
    if (hour.project !== inferred.timely_project_id) return false;

    // Ensure notes match (but only if set)
    const hasNotes = !!hour.note && !!inferred.note;
    const onlyOneNoteSet = (!!hour.note && !inferred.note) || (!hour.note && !!inferred.note);
    const matchNotes = onlyOneNoteSet || (!hasNotes && hour.note === inferred.note);

    if (!relaxed_note_matching && !matchNotes) return false;

    // Predictions without labels may match to hours with "any" label
    if (relaxed_tag_matching && !inferred.timely_label_ids?.length) return true;

    const matchEmptyHourLabels = relaxed_tag_matching && !hour.label_ids.length;
    const matchOnLabels = relaxed_tag_matching
      ? inferred.timely_label_ids?.some(id => hour.label_ids.includes(id))
      : isEqual(hour.label_ids, inferred.timely_label_ids);

    if (matchEmptyHourLabels || matchOnLabels) {
      return true;
    }

    return false;
  });

  return matchingHour?.id || null;
};
